import React, { useState, useEffect } from 'react';

import {
	ApButtonIcon,
	colors,
	ApMenuItem,
	ApMenu,
} from '@alixpartners/ui-components';
import { SimpleDonutChart } from 'components/SimpleDonutChart';
import { Text, CellText, InputText, EditInput } from './TableRow.styles';
import { CHART_COLORS } from 'styles/colors';
import {
	Cell,
	Row,
	CellNumber,
	SpanWrapper,
	RowSpan,
	SpanColumn,
	CellSmall,
	CellIcon,
	CellSort,
	CellSpacing,
	MoreIconWrapper,
} from '../ResultTable.styles';
import orderBy from 'lodash/orderBy';
import { ITEMS_TYPE, getWorkingCapital } from '../../../utils';
import isEqual from 'lodash/isEqual';

export const TableRow = ({
	companies,
	data,
	spendFormatter,
	showCompanySpend,
	onRemove,
	onInputChange,
	currencySymbol,
	itemsType,
	wacc,
	originalData,
	segments,
	showTopVendors,
}) => {
	const {
		name,
		spend,
		addressablePercentage,
		lowPercentage,
		highPercentage,
		opexPercentage,
		capexPercentage,
		targetDpo,
		weightedAverageDpo,
		workingCapitalImpact,
		workingCapital,
		tailSegmentsLowHigh,
		includeTailSpend,
	} = data;
	const [updates, setUpdates] = useState({
		addressablePercentage,
		lowPercentage,
		highPercentage,
		opexPercentage,
		capexPercentage,
		targetDpo,
		tailSegmentsLowHigh,
	});

	const totalSpend =
		itemsType === ITEMS_TYPE.CATEGORY
			? !includeTailSpend && data.headSpend != null
				? data.headSpend
				: spend
			: spend;

	useEffect(
		() => {
			const {
				addressablePercentage,
				lowPercentage,
				highPercentage,
				opexPercentage,
				capexPercentage,
				targetDpo,
				tailSegmentsLowHigh,
			} = data;

			setUpdates({
				addressablePercentage,
				lowPercentage,
				highPercentage,
				opexPercentage,
				capexPercentage,
				targetDpo,
				tailSegmentsLowHigh,
			});
		},
		[data]
	);

	const chartData = orderBy(
		companies
			.map((name, index) => ({
				name,
				value: data.companies[name] || 0,
				color: CHART_COLORS[index % CHART_COLORS.length],
			}))
			.filter(item => item.value),
		'value',
		'desc'
	);

	const onInputBlur = e => {
		const name = e.target.name;

		if (!isEqual(data[name], updates[name])) {
			onInputChange({ id: data.id, ...updates });
		}
	};

	const onCellEdit = e => {
		const name = e.target.name;
		let number = Number(e.target.value);

		if (number > 100) {
			number = 100;
		}

		if (number < 0) {
			number = 0;
		}

		setUpdates({
			...updates,
			[name]: number,
			...(name === 'opexPercentage' ? { capexPercentage: 100 - number } : {}),
			...(name === 'capexPercentage' ? { opexPercentage: 100 - number } : {}),
		});
	};

	// lowPercentage or highPercentage for tailSpend segment
	const onCellSegmentEdit = (segmentName, propName) => e => {
		let number = Number(e.target.value);

		if (number > 100) {
			number = 100;
		}

		if (number < 0) {
			number = 0;
		}

		setUpdates({
			...updates,
			tailSegmentsLowHigh: updates.tailSegmentsLowHigh.map(item => ({
				...item,
				...(item.name === segmentName ? { [propName]: number } : {}),
			})),
		});
	};

	const handleTargetDpoChange = e => {
		let number = Number(e.target.value);

		if (number < 0) {
			number = 0;
		}

		setUpdates({
			...updates,
			targetDpo: number,
		});
	};

	const getCompanyCellValue = companyName => {
		const spend = data.companies[companyName];
		let cellValue = spendFormatter(data.companies[companyName]);

		if (
			itemsType === ITEMS_TYPE.PT_HARMONIZATION &&
			spend &&
			originalData.companiesDpo
		) {
			const targetDpo = originalData.companiesDpo[companyName];
			cellValue = `${cellValue} (${targetDpo})`;
		}

		return cellValue;
	};

	const getTailSpendPercentage = (segmentName, propName) => {
		const currentSegment =
			(updates.tailSegmentsLowHigh &&
				updates.tailSegmentsLowHigh.find(item => item.name === segmentName)) ||
			{};

		return currentSegment[propName] || 0;
	};

	const getSegmentHighLowSpend = (segmentName, propName) => {
		if (!tailSegmentsLowHigh || !data.segments) {
			return 0;
		}

		const segmentSpend = data.segments[segmentName] || 0;
		const segmentConfig =
			tailSegmentsLowHigh.find(item => item.name === segmentName) || {};
		const percentage = segmentConfig[propName] || 0;

		return (percentage * segmentSpend) / 100;
	};


	const handleTopVendorsClick = () => {
		showTopVendors({
			id: data.groupName,
			name: data.name,
		});
	};

	const renderDefault = () => {
		const addressableValue = (addressablePercentage * totalSpend) / 100;
		const lowValue = (addressableValue * lowPercentage) / 100;
		const highValue = (addressableValue * highPercentage) / 100;

		return (
			<React.Fragment>
				<CellNumber>
					<span>{spendFormatter(totalSpend)}</span>
					<SimpleDonutChart
						data={chartData}
						currencySymbol={currencySymbol}
						paddingAngle={2}
					/>
				</CellNumber>
				{showCompanySpend &&
					companies.map((companyName, index) => (
						<CellNumber key={`${companyName}${index}`}>
							{getCompanyCellValue(companyName)}
						</CellNumber>
					))}
				<SpanWrapper>
					<RowSpan>
						<SpanColumn>
							<CellText>{spendFormatter(addressableValue)}</CellText>
						</SpanColumn>
						<SpanColumn>
							<InputText>
								<EditInput
									name="addressablePercentage"
									value={String(updates.addressablePercentage)}
									maxLength={3}
									onChange={onCellEdit}
									onBlur={onInputBlur}
									type="number"
								/>
								%
							</InputText>
						</SpanColumn>
					</RowSpan>
				</SpanWrapper>
				<SpanWrapper>
					<RowSpan>
						<SpanColumn>
							<CellText>{spendFormatter(lowValue)}</CellText>
						</SpanColumn>
						<SpanColumn>
							<InputText>
								<EditInput
									name="lowPercentage"
									value={String(updates.lowPercentage)}
									maxLength={3}
									onChange={onCellEdit}
									onBlur={onInputBlur}
									type="number"
								/>
								%
							</InputText>
						</SpanColumn>
					</RowSpan>
				</SpanWrapper>
				<SpanWrapper>
					<RowSpan>
						<SpanColumn>
							<CellText>{spendFormatter(highValue)}</CellText>
						</SpanColumn>
						<SpanColumn>
							<InputText>
								<EditInput
									name="highPercentage"
									value={String(updates.highPercentage)}
									maxLength={3}
									onChange={onCellEdit}
									onBlur={onInputBlur}
									type="number"
								/>
								%
							</InputText>
						</SpanColumn>
					</RowSpan>
				</SpanWrapper>
				<CellSmall>
					<InputText>
						<EditInput
							name="opexPercentage"
							value={String(updates.opexPercentage)}
							maxLength={3}
							onChange={onCellEdit}
							onBlur={onInputBlur}
							type="number"
						/>
						%
					</InputText>
				</CellSmall>
				<CellSmall>
					<InputText>
						<EditInput
							name="capexPercentage"
							value={String(updates.capexPercentage)}
							maxLength={3}
							onChange={onCellEdit}
							onBlur={onInputBlur}
							type="number"
						/>
						%
					</InputText>
				</CellSmall>
			</React.Fragment>
		);
	};

	const renderPT = () => {
		const workingCapitalValue =
			itemsType === ITEMS_TYPE.PT_HARMONIZATION
				? workingCapital != null
					? workingCapital * (wacc / 100)
					: workingCapitalImpact
				: getWorkingCapital(data, wacc);
		const wciLow = (workingCapitalValue * lowPercentage) / 100;
		const wciHigh = (workingCapitalValue * highPercentage) / 100;

		return (
			<React.Fragment>
				<CellNumber>
					<span>{spendFormatter(spend)}</span>
					<SimpleDonutChart
						data={chartData}
						currencySymbol={currencySymbol}
						paddingAngle={2}
					/>
				</CellNumber>
				{showCompanySpend &&
					companies.map((companyName, index) => (
						<CellNumber key={`${companyName}${index}`}>
							{getCompanyCellValue(companyName)}
						</CellNumber>
					))}
				{itemsType === ITEMS_TYPE.PT_IMPROVEMENT && (
					<React.Fragment>
						<CellSort>{weightedAverageDpo}</CellSort>
						<CellSort>
							<InputText>
								<EditInput
									name="targetDpo"
									value={String(updates.targetDpo)}
									maxLength={3}
									onChange={handleTargetDpoChange}
									onBlur={onInputBlur}
									type="number"
								/>
							</InputText>
						</CellSort>
					</React.Fragment>
				)}
				{itemsType === ITEMS_TYPE.PT_HARMONIZATION && (
					<CellSort>{targetDpo}</CellSort>
				)}
				<SpanWrapper>
					<RowSpan>
						<SpanColumn>
							<InputText>
								<EditInput
									name="lowPercentage"
									value={String(updates.lowPercentage)}
									maxLength={3}
									onChange={onCellEdit}
									onBlur={onInputBlur}
									type="number"
								/>
								%
							</InputText>
						</SpanColumn>
						<SpanColumn>
							<InputText>
								<EditInput
									name="highPercentage"
									value={String(updates.highPercentage)}
									maxLength={3}
									onChange={onCellEdit}
									onBlur={onInputBlur}
									type="number"
								/>
								%
							</InputText>
						</SpanColumn>
					</RowSpan>
				</SpanWrapper>
				<SpanWrapper>
					<RowSpan>
						<SpanColumn>
							<CellText>{spendFormatter(wciLow)}</CellText>
						</SpanColumn>
						<SpanColumn>
							<CellText>{spendFormatter(wciHigh)}</CellText>
						</SpanColumn>
					</RowSpan>
				</SpanWrapper>
			</React.Fragment>
		);
	};

	const renderTailSpend = () => {
		const addressableValue = (addressablePercentage * totalSpend) / 100;
		const lowValue = (addressableValue * lowPercentage) / 100;
		const highValue = (addressableValue * highPercentage) / 100;

		return (
			<React.Fragment>
				<SpanWrapper>
					<RowSpan>
						<CellNumber noBorder>
							<span>{spendFormatter(spend)}</span>
							<SimpleDonutChart
								data={chartData}
								currencySymbol={currencySymbol}
								paddingAngle={2}
							/>
						</CellNumber>
					</RowSpan>
				</SpanWrapper>

				{showCompanySpend &&
					companies.map((companyName, index) => (
						<SpanWrapper key={companyName}>
							<RowSpan>
								<CellNumber noBorder key={`${companyName}${index}`}>
									{getCompanyCellValue(companyName)}
								</CellNumber>
							</RowSpan>
						</SpanWrapper>
					))}

				<SpanWrapper>
					<RowSpan>
						<SpanColumn>
							<CellText>{spendFormatter(addressableValue)}</CellText>
						</SpanColumn>
						<SpanColumn>
							<InputText>
								<EditInput
									name="addressablePercentage"
									value={String(updates.addressablePercentage)}
									maxLength={3}
									onChange={onCellEdit}
									onBlur={onInputBlur}
									type="number"
								/>
								%
							</InputText>
						</SpanColumn>
					</RowSpan>
				</SpanWrapper>
				<SpanWrapper>
					<RowSpan>
						<SpanColumn>
							<CellText>{spendFormatter(lowValue)}</CellText>
						</SpanColumn>
						<SpanColumn>
							<InputText>
								<EditInput
									name="lowPercentage"
									value={String(updates.lowPercentage)}
									maxLength={3}
									onChange={onCellEdit}
									onBlur={onInputBlur}
									type="number"
								/>
								%
							</InputText>
						</SpanColumn>
					</RowSpan>
				</SpanWrapper>
				<SpanWrapper>
					<RowSpan>
						<SpanColumn>
							<CellText>{spendFormatter(highValue)}</CellText>
						</SpanColumn>
						<SpanColumn>
							<InputText>
								<EditInput
									name="highPercentage"
									value={String(updates.highPercentage)}
									maxLength={3}
									onChange={onCellEdit}
									onBlur={onInputBlur}
									type="number"
								/>
								%
							</InputText>
						</SpanColumn>
					</RowSpan>
				</SpanWrapper>
				{segments &&
					segments.map(({ name: segmentName }, index) => {
						const bgColor = index % 2 === 0 && colors.grey1;

						return (
							<React.Fragment key={`${segmentName}${name}`}>
								<SpanWrapper bgColor={bgColor}>
									<RowSpan>
										<SpanColumn>
											<CellText>
												{spendFormatter(
													data.segments && data.segments[segmentName]
												)}
											</CellText>
										</SpanColumn>
									</RowSpan>
								</SpanWrapper>
								<SpanWrapper bgColor={bgColor}>
									<RowSpan>
										<SpanColumn>
											{data.segments &&
											data.segments[segmentName] &&
											tailSegmentsLowHigh ? (
												<CellSmall noBorder>
													<InputText>
														<EditInput
															name="tailSegmentsLowHigh"
															value={String(
																getTailSpendPercentage(
																	segmentName,
																	'lowPercentage'
																)
															)}
															maxLength={3}
															onChange={onCellSegmentEdit(
																segmentName,
																'lowPercentage'
															)}
															onBlur={onInputBlur}
															type="number"
														/>
														%
													</InputText>
												</CellSmall>
											) : (
												<CellText>-</CellText>
											)}
										</SpanColumn>
										<SpanColumn>
											<CellText>
												{spendFormatter(
													getSegmentHighLowSpend(segmentName, 'lowPercentage')
												)}
											</CellText>
										</SpanColumn>
									</RowSpan>
								</SpanWrapper>
								<SpanWrapper bgColor={bgColor} borderColor={colors.grey6}>
									<RowSpan>
										<SpanColumn>
											{data.segments &&
											data.segments[segmentName] &&
											tailSegmentsLowHigh ? (
												<CellSmall noBorder>
													<InputText>
														<EditInput
															name="tailSegmentsLowHigh"
															value={String(
																getTailSpendPercentage(
																	segmentName,
																	'highPercentage'
																)
															)}
															maxLength={3}
															onChange={onCellSegmentEdit(
																segmentName,
																'highPercentage'
															)}
															onBlur={onInputBlur}
															type="number"
														/>
														%
													</InputText>
												</CellSmall>
											) : (
												<CellText>-</CellText>
											)}
										</SpanColumn>
										<SpanColumn>
											<CellText>
												{spendFormatter(
													getSegmentHighLowSpend(segmentName, 'highPercentage')
												)}
											</CellText>
										</SpanColumn>
									</RowSpan>
								</SpanWrapper>
							</React.Fragment>
						);
					})}
				<CellSmall
					borderColor={colors.grey6}
					bgColor={segments.length % 2 === 0 && colors.grey1}
				>
					<InputText>
						<EditInput
							name="opexPercentage"
							value={String(updates.opexPercentage)}
							maxLength={3}
							onChange={onCellEdit}
							onBlur={onInputBlur}
							type="number"
						/>
						%
					</InputText>
				</CellSmall>
				<CellSmall bgColor={segments.length % 2 === 1 && colors.grey1}>
					<InputText>
						<EditInput
							name="capexPercentage"
							value={String(updates.capexPercentage)}
							maxLength={3}
							onChange={onCellEdit}
							onBlur={onInputBlur}
							type="number"
						/>
						%
					</InputText>
				</CellSmall>
			</React.Fragment>
		);
	};

	const displayTopVendors =
		itemsType === ITEMS_TYPE.PT_IMPROVEMENT ||
		itemsType === ITEMS_TYPE.CATEGORY ||
		itemsType === ITEMS_TYPE.TAIL_SPEND;

	return (
		<Row>
			<Cell>
				{displayTopVendors && (
					<MoreIconWrapper>
						<ApMenu
							placement="bottom-start"
							button={
								<ApButtonIcon
									iconName="outline_more_vert"
									onClick={e => e.stopPropagation()}
								/>
							}
						>
							<ApMenuItem onClick={handleTopVendorsClick}>
								Top vendors
							</ApMenuItem>
							<ApMenuItem
								onClick={() => {
									onRemove(data);
								}}
							>
								Remove
							</ApMenuItem>
						</ApMenu>
					</MoreIconWrapper>
				)}

				<Text>{name}</Text>
			</Cell>

			{itemsType === ITEMS_TYPE.PT_IMPROVEMENT ||
			itemsType === ITEMS_TYPE.PT_HARMONIZATION
				? renderPT()
				: itemsType === ITEMS_TYPE.TAIL_SPEND
					? renderTailSpend()
					: renderDefault()}

			{!displayTopVendors ? (
				<CellIcon>
					<ApButtonIcon
						iconName="baseline_close"
						onClick={() => onRemove(data)}
					/>
				</CellIcon>
			) : (
				<CellSpacing />
			)}
		</Row>
	);
};

export default TableRow;
